import { MigrationCostSummary, OutputMigration } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Grid, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals } from '../../common/LocalizationHelper';
import { getCell } from '../common/awsui-table-helper';
import MigrationSummaryCostGraphs from './MigrationSummaryCostGraphs';

interface Props {
    migration: OutputMigration;
}

function MigrationSummaryCost({migration}: Props): JSX.Element {
    const currency = migration.currency;

    const columnDefs: TableProps.ColumnDefinition<MigrationCostSummary>[] = [
        {
            id: 'strategy',
            header: constants.STRATEGY,
            cell: item => item.strategy,
        },
        {
            id: 'partnerResourceCost',
            header: <Box float='right'>{constants.PARTNER_RESOURCE_COST}</Box>,
            cell: item => {
                const value = formatCurrencyNoDecimals(item.totalPartnerCost, currency);
                return getCell(value, item.strategy === constants.TOTAL);
            },
        },
        {
            id: 'customerResourceCost',
            header: <Box float='right'>{constants.CUSTOMER_RESOURCE_COST}</Box>,
            cell: item => {
                const value = formatCurrencyNoDecimals(item.totalCustomerCost, currency);
                return getCell(value, item.strategy === constants.TOTAL);
            }
        },
        {
            id: 'awsResourceCost',
            header: <Box float='right'>{constants.AWS_RESOURCE_COST}</Box>,
            cell: item => {
                const value = formatCurrencyNoDecimals(item.totalAwsCost, currency);
                return getCell(value, item.strategy === constants.TOTAL);
            }
        },
        {
            id: 'totalResourceCost',
            header: <Box float='right'>{constants.TOTAL_RESOURCE_COST}</Box>,
            cell: item => {
                const value = formatCurrencyNoDecimals(item.totalExecutionCost, currency);
                return getCell(value, item.strategy === constants.TOTAL);
            }
        }
    ];

    const tableItems: MigrationCostSummary[] = [...migration.summary].filter(item => item.totalExecutionCost > 0);
    tableItems.push({
        strategy: constants.TOTAL,
        totalPartnerCost: migration.totalResourceCosts[0].partner,
        totalCustomerCost: migration.totalResourceCosts[0].customer,
        totalAwsCost: migration.totalResourceCosts[0].aws,
        totalExecutionCost: migration.totalServerMigrationCost,
    });

    const costsTable = (
        <Table
            columnDefinitions={columnDefs}
            items={tableItems}
        />
    );

    const gridDef = [{colspan: 6}, {colspan: 6}];
    const totalsList = (
        <Box padding={{left: 'l', right: 'l'}}> 
            <SpaceBetween size='xs'>
                <Grid 
                    gridDefinition={gridDef}
                >
                    <Box fontSize='heading-m' fontWeight='bold'>{constants.TOTAL_RESOURCE_COST}</Box>
                    <Box fontSize='heading-m' float='right' fontWeight='bold'>
                        {formatCurrencyNoDecimals(migration.totalServerMigrationCost, currency)}
                    </Box>
                </Grid>
                <Grid 
                    gridDefinition={gridDef}
                >
                    <Box fontSize='heading-m' fontWeight='bold'>{constants.TOOLS_TRAINING_OTHER_COST}</Box>
                    <Box fontSize='heading-m' float='right' fontWeight='bold'>
                        {formatCurrencyNoDecimals(migration.additionalCosts, currency)}
                    </Box>
                </Grid>
                <Grid 
                    gridDefinition={gridDef}
                >
                    <Box fontSize='heading-m' fontWeight='bold'>{constants.TOTAL_MIGRATION_COST}</Box>
                    <Box fontSize='heading-m' float='right' fontWeight='bold'>
                        {formatCurrencyNoDecimals(migration.totalMigrationCost, currency)}
                    </Box>
                </Grid>
            </SpaceBetween>
        </Box>
    );

    return (
        <SpaceBetween size='m'>
            {costsTable}
            {totalsList}
            <MigrationSummaryCostGraphs migration={migration} currency={currency}/>
        </SpaceBetween>
    );
}

export default MigrationSummaryCost;
